import { ChangeEvent, useCallback, useState, useEffect } from "react";
import { Button } from "../Button";
import { Image, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../service/api";

interface EventGalleryInputProps {
  eventId?: string;
  onPhotosChange?: (photos: File[]) => void;
  disabled?: boolean;
  isEditing?: boolean;
  existingPhotos?: any[];
}

interface GalleryPhoto {
  id?: string;
  file?: File;
  url?: string;
  isExisting?: boolean;
  isUploading?: boolean;
  isDeleting?: boolean;
}

export function EventGalleryInput({
  eventId,
  onPhotosChange,
  disabled = false,
  isEditing = false,
  existingPhotos = [],
}: EventGalleryInputProps) {
  const [photos, setPhotos] = useState<GalleryPhoto[]>(() => {
    // Carregar fotos existentes do prop
    if (existingPhotos && existingPhotos.length > 0) {
      return existingPhotos.map((photo) => ({
        id: photo.id,
        url: photo.url,
        isExisting: true,
      }));
    }
    return [];
  });
  
  const [dragActive, setDragActive] = useState(false);

  // Update photos when existingPhotos prop changes
  useEffect(() => {
    if (existingPhotos && existingPhotos.length > 0) {
      const existingGalleryPhotos: GalleryPhoto[] = existingPhotos.map((photo) => ({
        id: photo.id,
        url: photo.url,
        isExisting: true,
      }));
      
      setPhotos(prev => {
        // Manter fotos novas (não existentes) e substituir as existentes
        const newPhotos = prev.filter(p => !p.isExisting);
        return [...existingGalleryPhotos, ...newPhotos];
      });
    }
  }, [existingPhotos]);

  const handleFileSelect = (files: File[]) => {
    const newPhotos = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      isExisting: false,
    }));

    const updatedPhotos = [...photos, ...newPhotos];
    setPhotos(updatedPhotos);

    // Notificar componente pai sobre mudança
    if (onPhotosChange) {
      const filePhotos = updatedPhotos.filter(p => p.file).map(p => p.file!);
      onPhotosChange(filePhotos);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const validFiles = Array.from(files).filter(file => {
        const isValidType = file.type.startsWith('image/');
        const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
        
        if (!isValidType) {
          toast.error("Apenas arquivos de imagem são permitidos");
          return false;
        }
        
        if (!isValidSize) {
          toast.error("O arquivo deve ter no máximo 5MB");
          return false;
        }
        
        return true;
      });

      if (validFiles.length > 0) {
        handleFileSelect(validFiles);
        // Reset do input para permitir selecionar os mesmos arquivos novamente
        event.target.value = '';
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      
      if (!isValidType) {
        toast.error("Apenas arquivos de imagem são permitidos");
        return false;
      }
      
      if (!isValidSize) {
        toast.error("O arquivo deve ter no máximo 5MB");
        return false;
      }
      
      return true;
    });

    if (validFiles.length > 0) {
      handleFileSelect(validFiles);
    }
  };

  // Remove photo from gallery
  const removePhoto = async (index: number) => {
    const photo = photos[index];
    
    if (photo.isExisting && photo.id) {
      // Marcar foto como sendo excluída
      setPhotos(prev => prev.map((p, i) => 
        i === index ? { ...p, isDeleting: true } : p
      ));
      
      try {
        await api.delete('events/remove-photo', {
          data: { photo_id: photo.id }
        });
        toast.success("Foto removida com sucesso");
      } catch (error) {
        toast.error("Erro ao remover foto");
        
        // Reverter estado se houver erro
        setPhotos(prev => prev.map((p, i) => 
          i === index ? { ...p, isDeleting: false } : p
        ));
        return;
      }
    }

    // Revogar URL se for um arquivo local
    if (photo.url && !photo.isExisting) {
      URL.revokeObjectURL(photo.url);
    }

    const updatedPhotos = photos.filter((_, i) => i !== index);
    setPhotos(updatedPhotos);

    // Notificar componente pai sobre mudança
    if (onPhotosChange) {
      const filePhotos = updatedPhotos.filter(p => p.file).map(p => p.file!);
      onPhotosChange(filePhotos);
    }
  };

  // Upload photos to gallery
  const uploadPhotos = useCallback(async () => {
    if (!eventId) {
      return;
    }
    
    const newPhotos = photos.filter(p => p.file && !p.isExisting);
    
    if (newPhotos.length === 0) {
      toast.info("Nenhuma foto nova para enviar");
      return;
    }
    
    let formData = new FormData();
    formData.append('event_id', eventId);

    newPhotos.forEach((photo, index) => {
      if (photo.file) {
        formData.append('photos', photo.file);
      } else {
        console.log(`❌ [DEBUG UPLOAD] Photo ${index + 1} has no file!`);
      }
    });

    try {
      setPhotos(prev => prev.map(p => p.file ? { ...p, isUploading: true } : p));
      
      // Usar POST para novo evento ou PATCH para edição
      const method = isEditing ? 'patch' : 'post';
      const endpoint = 'events/add-photos';
      
      await api[method](endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Marcar fotos como existentes após upload
      setPhotos(prev => prev.map(p => 
        p.file ? { ...p, isExisting: true, isUploading: false } : p
      ));
      
      toast.success("Fotos enviadas com sucesso");
    } catch (error) {
      console.error("❌ [DEBUG UPLOAD] Error during upload:", error);
      setPhotos(prev => prev.map(p => ({ ...p, isUploading: false })));
      toast.error("Erro ao enviar fotos");
    }
  }, [eventId, photos]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 lg:grid-cols-form">
        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
          Galeria de fotos
        </label>
        <div className="space-y-4">
          {/* Área de upload */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${dragActive 
                ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' 
                : 'border-zinc-300 dark:border-zinc-700 hover:border-violet-400 dark:hover:border-violet-500'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleInputChange}
              disabled={disabled}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            <div className="flex flex-col items-center gap-2">
              <div className="p-3 bg-zinc-100 dark:bg-zinc-800 rounded-full">
                <Image className="w-6 h-6 text-zinc-600 dark:text-zinc-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Clique para selecionar ou arraste as fotos aqui
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  PNG, JPG, JPEG até 5MB cada
                </p>
              </div>
            </div>
          </div>

          {/* Grid de fotos */}
          {photos.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {photos.map((photo, index) => (
                <div
                  key={`${photo.id || index}`}
                  className={`
                    relative group aspect-square rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800
                    ${photo.isUploading || photo.isDeleting ? 'opacity-50' : ''}
                  `}
                >
                  <img
                    src={photo.url}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay com botão de remoção */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removePhoto(index)}
                      disabled={disabled || photo.isUploading || photo.isDeleting}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white hover:bg-red-50 border-red-200 hover:border-red-300 px-2 py-2"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>

                  {/* Indicador de upload */}
                  {photo.isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}

                  {/* Indicador de exclusão */}
                  {photo.isDeleting && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}

                  {/* Indicador de foto existente */}
                  {photo.isExisting && !photo.isUploading && !photo.isDeleting && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                      Salva
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Botão de envio para fotos novas */}
          {eventId && photos.some(p => p.file && !p.isExisting) && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                onClick={uploadPhotos}
                disabled={disabled || photos.some(p => p.isUploading || p.isDeleting)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Enviar Fotos
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 